import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { useToast } from "../ui/use-toast";
import { Plus, Trash2, Upload, File, Image, Music } from "lucide-react";
import { Question, QuestionApiRequest } from "../../types/question";
import { questionService } from "../../services/questionService";

interface QuestionModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  onSave?: (questions: Question[]) => void;
  initialQuestions?: Question[];
  reelId: string;
}

const ACCEPTED_FILE_TYPES = {
  pdf: '.pdf',
  image: '.jpg,.jpeg,.png,.gif',
  audio: '.mp3,.wav,.ogg'
};

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

export function QuestionModal({
  isOpen = false,
  onClose = () => {},
  onSave = () => {},
  initialQuestions = [],
  reelId,
}: QuestionModalProps) {
  const { toast } = useToast();

  // Helper to get file type from filePath
  function getFileTypeFromPath(path: string): 'pdf' | 'image' | 'audio' | undefined {
    if (!path) return undefined;
    const ext = path.split('.').pop()?.toLowerCase();
    if (["jpg", "jpeg", "png", "gif"].includes(ext || "")) return "image";
    if (["mp3", "wav", "ogg"].includes(ext || "")) return "audio";
    if (ext === "pdf") return "pdf";
    return undefined;
  }

  // Map initialQuestions to use filePath only
  const mapInitialQuestions = (questions: any[]): Question[] => {
    return questions.map(q => ({
      ...q,
      filePath: q.filePath || ""
    }));
  };

  const [questions, setQuestions] = React.useState<Question[]>(
    Array.isArray(initialQuestions) && initialQuestions.length > 0
      ? mapInitialQuestions(initialQuestions)
      : [
          {
            id: Date.now().toString() + Math.random().toString(36).slice(2),
            question: "",
            options: [""],
            correctAnswer: "",
            filePath: "",
            sequenceNumber: 1,
            subModuleId: parseInt(reelId),
          },
        ]
  );

  const handleOptionChange = (questionId: string, index: number, value: string) => {
    setQuestions(questions.map(q => {
      if (q.id === questionId) {
        const newOptions = [...q.options];
        newOptions[index] = value;
        return { ...q, options: newOptions };
      }
      return q;
    }));
  };

  const addOption = (questionId: string) => {
    setQuestions(questions.map(q => {
      if (q.id === questionId) {
        return { ...q, options: [...q.options, ""] };
      }
      return q;
    }));
  };

  const removeOption = (questionId: string, optionIndex: number) => {
    setQuestions(questions.map(q => {
      if (q.id === questionId) {
        const newOptions = q.options.filter((_, index) => index !== optionIndex);
        return {
          ...q,
          options: newOptions,
          correctAnswer: q.correctAnswer === q.options[optionIndex] ? "" : q.correctAnswer
        };
      }
      return q;
    }));
  };

  const addQuestion = () => {
    const maxSeq = questions.reduce((max, q) => q.sequenceNumber > max ? q.sequenceNumber : max, 0);
    setQuestions([
      ...questions,
      {
        id: Date.now().toString() + Math.random().toString(36).slice(2),
        question: "",
        options: [""],
        correctAnswer: "",
        filePath: "",
        sequenceNumber: maxSeq + 1,
        subModuleId: parseInt(reelId),
      },
    ]);
  };

  // State for delete confirmation popup
  const [pendingDeleteQuestionId, setPendingDeleteQuestionId] = useState<string | null>(null);

  const handleDeleteQuestion = async (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    // Check if the question has a valid numeric id (saved in backend)
    const numericId = Number(questionId);
    if (question && Number.isInteger(numericId) && numericId > 0) {
      try {
        setLoading(true);
        await questionService.deleteQuestion(numericId);
        toast({
          title: "Success",
          description: "Question deleted successfully",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to delete question",
          variant: "destructive",
        });
        setLoading(false);
        setPendingDeleteQuestionId(null);
        return;
      }
      setLoading(false);
    }
    // Remove from UI (for both new and deleted questions)
    const updatedQuestions = questions.filter(q => q.id !== questionId);
    // Update sequence numbers after removal
    const reorderedQuestions = updatedQuestions.map((q, index) => ({
      ...q,
      sequenceNumber: index + 1
    }));
    setQuestions(reorderedQuestions);
    setPendingDeleteQuestionId(null);
  };

  const handleFileUpload = async (questionId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      toast({
        title: "Error",
        description: "File size should be less than 10MB",
        variant: "destructive",
      });
      return;
    }

    // Determine file type
    let fileType: 'pdf' | 'image' | 'audio';
    if (file.type.startsWith('image/')) {
      fileType = 'image';
    } else if (file.type === 'application/pdf') {
      fileType = 'pdf';
    } else if (file.type.startsWith('audio/')) {
      fileType = 'audio';
    } else {
      toast({
        title: "Error",
        description: "Unsupported file type. Please upload PDF, Image, or Audio files only.",
        variant: "destructive",
      });
      return;
    }

    // Upload file and get URL
    try {
      const response = await questionService.uploadFile(file);
      setQuestions(questions.map(q =>
        q.id === questionId
          ? { ...q, filePath: response.url }
          : q
      ));
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to upload file",
        variant: "destructive",
      });
      }
  };

  const removeFile = (questionId: string) => {
    setQuestions(questions.map(q =>
      q.id === questionId
        ? { ...q, filePath: "" }
        : q
    ));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
  };

  // Add loading state
  const [loading, setLoading] = useState(false);
  // Track last saved state for each question
  const [lastSavedQuestions, setLastSavedQuestions] = useState<Question[]>([]);

  // Ensure questions state updates when initialQuestions changes (for correct preview)
  React.useEffect(() => {
    if (Array.isArray(initialQuestions) && initialQuestions.length > 0) {
      setQuestions(mapInitialQuestions(initialQuestions));
      setLastSavedQuestions(mapInitialQuestions(initialQuestions));
    }
  }, [initialQuestions]);

  // Helper to check if a question is dirty (has unsaved changes)
  const isQuestionDirty = (q: Question) => {
    const last = lastSavedQuestions.find(sq => sq.id === q.id);
    if (!last) return true;
    return (
      q.question !== last.question ||
      q.correctAnswer !== last.correctAnswer ||
      q.sequenceNumber !== last.sequenceNumber ||
      q.filePath !== last.filePath ||
      JSON.stringify(q.options) !== JSON.stringify(last.options)
    );
  };

  const handleSaveQuestion = async (question: Question) => {
    // Validate question
    if (!question.question.trim()) {
      toast({
        title: "Error",
        description: "Question must be filled",
        variant: "destructive",
      });
      return;
    }
    if (question.options.some((opt) => !opt.trim())) {
      toast({
        title: "Error",
        description: "All options must be filled",
        variant: "destructive",
      });
      return;
    }
    if (!Array.isArray(question.options) || question.options.length < 2) {
      toast({
        title: "Error",
        description: "Please add at least 2 options.",
        variant: "destructive",
      });
      return;
    }
    if (question.correctAnswer === undefined || question.correctAnswer === "") {
      toast({
        title: "Error",
        description: "Please select a correct answer",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);

      // Determine questionType based on filePath
      let questionType = "text";
      if (question.filePath) {
        const type = getFileTypeFromPath(question.filePath);
        if (type) questionType = type;
      }

      // Prepare question data for API
      let numericId = Number(question.id);
      if (!Number.isInteger(numericId) || numericId <= 0) numericId = 0;
      const correctAnswerIndex = Array.isArray(question.options)
        ? (() => {
            const idx = question.options.findIndex(opt => opt === question.correctAnswer);
            return idx !== -1 ? idx + 1 : -1;
          })()
        : -1;
      const questionData: QuestionApiRequest = {
        id: numericId,
        subModuleId: parseInt(reelId),
        questionText: question.question,
        questionType,
        options: question.options,
        correctAnswer: correctAnswerIndex,
        sequenceNumber: question.sequenceNumber,
        filePath: question.filePath || "",
        modifiedOn: new Date().toISOString(),
      };

      // Save single question
      await questionService.saveQuestion(questionData);
      toast({
        title: "Success",
        description: "Question saved successfully",
      });
      onSave([question]);
      // Update last saved state for this question
      setLastSavedQuestions(prev => {
        const others = prev.filter(qs => qs.id !== question.id);
        return [...others, { ...question }];
      });
      // Fetch all questions for this submodule and update state
      try {
        const allQuestions = await questionService.getQuestions(parseInt(reelId));
        setQuestions(allQuestions);
        setLastSavedQuestions(allQuestions);
      } catch (fetchError) {
        // Optionally show a toast or log error
      }
    } catch (error) {
      console.error('Error saving question:', error);
      toast({
        title: "Error",
        description: "Failed to save question",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Questions</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6 overflow-y-auto flex-1 pr-2">
          {questions.map((q, qIndex) => (
            <div key={q.id} className="space-y-4 p-4 border rounded-lg">
              <div className="flex justify-between items-start">
                <Label className="text-lg">Question {qIndex + 1}</Label>
                <div className="flex items-center space-x-2">
                  <Label htmlFor={`sequence-${q.id}`} className="text-sm text-gray-500">Sequence:</Label>
                  <Input
                    id={`sequence-${q.id}`}
                    type="number"
                    min={1}
                    value={q.sequenceNumber}
                    onChange={(e) => {
                      const newSequence = parseInt(e.target.value) || 1;
                      setQuestions(questions.map(question => {
                        if (question.id === q.id) {
                          return { ...question, sequenceNumber: newSequence };
                        }
                        // If another question has this sequence number, increment it
                        if (question.sequenceNumber >= newSequence) {
                          return { ...question, sequenceNumber: question.sequenceNumber + 1 };
                        }
                        return question;
                      }).sort((a, b) => a.sequenceNumber - b.sequenceNumber));
                    }}
                    className="w-20"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setPendingDeleteQuestionId(q.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Textarea
                  value={q.question}
                  onChange={(e) =>
                    setQuestions(questions.map(question =>
                      question.id === q.id
                        ? { ...question, question: e.target.value }
                        : question
                    ))
                  }
                  placeholder="Enter your question here..."
                  className="min-h-[100px]"
                />
              </div>

              {/* File Upload Section */}
              <div className="space-y-2">
                <Label>Attachment (Select one file: PDF, Image, or Audio)</Label>
                <div className="flex gap-2">
                  <Input
                    type="file"
                    accept={`${ACCEPTED_FILE_TYPES.pdf},${ACCEPTED_FILE_TYPES.image},${ACCEPTED_FILE_TYPES.audio}`}
                    onChange={(e) => handleFileUpload(q.id, e)}
                    className="flex-1"
                    disabled={!!q.filePath}
                  />
                  {q.filePath && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(q.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                {/* Single Attachment Preview */}
                {q.filePath && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center">
                        {getFileTypeFromPath(q.filePath) === 'image' ? (
                          <img
                            src={q.filePath}
                            alt="Preview"
                            className="h-10 w-10 object-cover rounded mr-2"
                          />
                        ) : getFileTypeFromPath(q.filePath) === 'pdf' ? (
                          <a href={q.filePath} target="_blank" rel="noopener noreferrer" className="flex items-center">
                            <File className="h-5 w-5 mr-2" />
                            <span className="text-sm">View PDF</span>
                          </a>
                        ) : getFileTypeFromPath(q.filePath) === 'audio' ? (
                          <audio controls src={q.filePath} className="h-10 w-32 mr-2" />
                        ) : (
                          <div className="h-10 w-10 flex items-center justify-center bg-gray-200 rounded mr-2">
                              <File className="h-5 w-5" />
                          </div>
                        )}
                        <span className="text-sm">{q.filePath.split("/").pop()}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Options</Label>
                {(Array.isArray(q.options) ? q.options : []).map((option, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <Input
                      value={option}
                      onChange={(e) => handleOptionChange(q.id, index, e.target.value)}
                      placeholder={`Option ${index + 1}`}
                    />
                    <input
                      type="radio"
                      name={`correctAnswer-${q.id}`}
                      checked={q.correctAnswer === option}
                      onChange={() =>
                        setQuestions(questions.map(question =>
                          question.id === q.id
                            ? { ...question, correctAnswer: option }
                            : question
                        ))
                      }
                      className="h-4 w-4"
                    />
                    <Label>Correct</Label>
                    {(Array.isArray(q.options) ? q.options.length : 0) > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeOption(q.id, index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addOption(q.id)}
                  className="mt-2"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Option
                </Button>
              </div>

              <div className="flex justify-end mt-4">
                <Button
                  type="button"
                  onClick={() => handleSaveQuestion(q)}
                  disabled={loading || !isQuestionDirty(q)}
                  className="bg-black hover:bg-gray-800 text-white"
                >
                  {loading ? "Saving..." : "Save Question"}
                </Button>
              </div>
            </div>
          ))}

          <Button
            type="button"
            variant="outline"
            onClick={addQuestion}
            className="w-full"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Question
          </Button>
        </form>

        <DialogFooter className="mt-4">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!pendingDeleteQuestionId} onOpenChange={() => setPendingDeleteQuestionId(null)}>
        <DialogContent className="max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete Question</DialogTitle>
          </DialogHeader>
          <div>Are you sure you want to delete this question? This action cannot be undone.</div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setPendingDeleteQuestionId(null)} disabled={loading}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => pendingDeleteQuestionId && handleDeleteQuestion(pendingDeleteQuestionId)} disabled={loading}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
} 